import { useEffect, useState, type ReactNode } from 'react';
import logoUrl from '/logo.png';
import AccountWidget from './AccountWidget';

/**
 * The signed-in shell: a rail on the left, one page at a time on the right.
 *
 * Signed out, nothing changes. That surface is the marketing page, the SEO
 * surface and where the LinkedIn post lands, and it keeps the site chrome that
 * D-23 asks for. **This shell is only what a signed-in client sees on their own
 * account**, where the top nav is five links to pages they are not currently
 * using and the screen is a single column of stacked panels with no way to jump
 * between them.
 *
 * ## Why the rail carries labels
 *
 * The design this follows uses an icon-only rail, which works in a desktop app
 * somebody opens daily. A visibility scan is opened monthly, and an icon with no
 * label is how a monthly user clicks three things to find the fourth. Labels at
 * rest on desktop; the rail collapses to icons if somebody wants the room, and
 * that choice is remembered.
 *
 * ## Why the site nav moves rather than disappears
 *
 * A signed-in client still has to be able to reach the marketing site, not least
 * "Work with me", which exists so a visitor can find the person behind the tool.
 * It sits at the bottom of the rail rather than across the top.
 */

export interface DashboardPage {
  id: string;
  label: string;
  /** One line under the title. What this page answers, not what it contains. */
  blurb: string;
  icon: ReactNode;
  /** Hidden for a read-only viewer. */
  ownerOnly?: boolean;
  /**
   * Rail grouping.
   *
   * Nine items in one list is a wall. Three small groups is a menu: the tool,
   * the settings behind it, and the reading. The labels disappear when the rail
   * is collapsed, where a divider does the same job in less room.
   */
  group: 'tool' | 'setup' | 'learn';
}

const GROUP_LABEL: Record<DashboardPage['group'], string> = {
  tool: 'Tool',
  setup: 'Setup',
  learn: 'Background',
};

const COLLAPSE_KEY = 'aivs_rail_collapsed';

export default function DashboardShell({ pages, current, onNavigate, account, viewer, children }: {
  pages: DashboardPage[];
  current: string;
  onNavigate: (id: string) => void;
  account: { email: string; tier: string };
  viewer: boolean;
  children: ReactNode;
}): JSX.Element {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  // Mobile: the rail is a drawer, closed by default and closed again on
  // navigation, because leaving it open over the page you just chose is the
  // most common mobile menu bug.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  // Escape closes the drawer. A drawer with no keyboard exit is a trap.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const visible = pages.filter((p) => !(viewer && p.ownerOnly));
  const go = (id: string): void => { onNavigate(id); setOpen(false); };

  const rail = (
    <nav
      aria-label="Dashboard"
      className={`flex flex-col h-full bg-warm-dark-2/40 border-r border-warm-dark-2 ${collapsed ? 'w-16' : 'w-60'} transition-[width] duration-150`}
    >
      <div className="px-3 py-4 border-b border-warm-dark-2">
        <a
          href="https://pagecoder.ai"
          className={`flex items-center gap-2 font-semibold ${collapsed ? 'justify-center' : ''}`}
          title="PageCoder.ai"
        >
          <img src={logoUrl} alt="" width={28} height={28} className="shrink-0" aria-hidden="true" />
          {!collapsed && <span className="text-sm tracking-tight">AI Visibility Scan</span>}
        </a>
      </div>

      <div className="flex-1 py-1 overflow-y-auto">
        {(['tool', 'setup', 'learn'] as const).map((g) => {
          const items = visible.filter((p) => p.group === g);
          if (items.length === 0) return null;
          return (
            <div key={g} className="py-1">
              {collapsed
                ? <div className="mx-3 my-1.5 border-t border-warm-dark-2" aria-hidden="true" />
                : (
                  <p className="px-3 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-text-secondary/50">
                    {GROUP_LABEL[g]}
                  </p>
                )}
              <ul>
                {items.map((p) => {
                  const active = p.id === current;
                  return (
                    <li key={p.id}>
                      <button
                        onClick={() => go(p.id)}
                        aria-current={active ? 'page' : undefined}
                        title={collapsed ? p.label : undefined}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition border-l-2 ${
                          active
                            ? 'border-cyan bg-cyan/10 text-text-primary'
                            : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-warm-dark-2/60'
                        } ${collapsed ? 'justify-center' : ''}`}
                      >
                        <span className="shrink-0 w-5 flex justify-center" aria-hidden="true">{p.icon}</span>
                        {!collapsed && <span className="truncate">{p.label}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <div className="border-t border-warm-dark-2 py-2">
        {/* Back to the site. A signed-in client must still be able to reach the
            pages that say who builds this. */}
        {/* Off-site only. Scanning is a page in the rail above, and listing it
            twice made the menu look longer than it is. */}
        {[
          { href: 'https://pagecoder.ai/about', label: 'Work with me', icon: '☺' },
          { href: 'https://pagecoder.ai', label: 'PageCoder.ai', icon: '↗' },
        ].map((l) => (
          <a
            key={l.href}
            href={l.href}
            title={collapsed ? l.label : undefined}
            className={`flex items-center gap-3 px-3 py-2 text-sm text-text-secondary hover:text-cyan transition ${collapsed ? 'justify-center' : ''}`}
          >
            <span className="shrink-0 w-5 flex justify-center" aria-hidden="true">{l.icon}</span>
            {!collapsed && <span className="truncate">{l.label}</span>}
          </a>
        ))}

        {/* Only the shared-view marker. The plan and the address are in the
            account control at the top right, and saying them twice on one
            screen is how a layout starts looking unconsidered. */}
        {viewer && (
          <div className={`px-3 pt-2 mt-1 border-t border-warm-dark-2 ${collapsed ? 'text-center' : ''}`}>
            <p className="font-mono text-[11px] text-cyan/80" title={account.email}>
              {collapsed ? '◍' : 'shared view'}
            </p>
          </div>
        )}

        <button
          onClick={() => setCollapsed((v) => !v)}
          className="hidden lg:flex items-center gap-3 px-3 py-2 mt-1 w-full text-sm text-text-secondary/70 hover:text-text-primary transition"
          aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <span className="shrink-0 w-5 flex justify-center" aria-hidden="true">{collapsed ? '›' : '‹'}</span>
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-warm-dark">
      {/* Desktop rail, sticky so it survives a long history page. */}
      <div className="hidden lg:block sticky top-0 h-screen shrink-0">{rail}</div>

      {/* Mobile drawer. */}
      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/60"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          {/*
            Solid ground under the rail.

            The rail's own background is a 40% tint, which is opaque enough on
            desktop because it composites over the page surface. As a drawer it
            floats over the content, and 40% meant the page read straight
            through the menu. The wrapper supplies what the desktop layout was
            supplying.
          */}
          <div className="lg:hidden fixed inset-y-0 left-0 z-50 bg-warm-dark">{rail}</div>
        </>
      )}

      <div className="flex-1 min-w-0">
        {/*
          A thin bar carrying only the account control, plus the menu button on
          mobile.

          Not a nav: the rail is the nav. This exists because the account
          control is where people expect it on every other screen of the site,
          and moving it into the rail would have made signing out a hunt. It is
          the same `AccountWidget` the site header uses, not a copy of its
          markup, so the two cannot drift.
        */}
        <div className="flex items-center justify-between gap-3 px-4 lg:px-8 py-3 border-b border-warm-dark-2">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setOpen(true)}
              aria-label="Open the menu"
              className="lg:hidden font-mono text-lg text-text-secondary hover:text-text-primary"
            >≡</button>
            <span className="lg:hidden text-sm text-text-primary truncate">
              {visible.find((p) => p.id === current)?.label ?? 'Dashboard'}
            </span>
          </div>
          <AccountWidget />
        </div>

        <main className="px-5 lg:px-8 py-6 lg:py-8 max-w-6xl">{children}</main>
      </div>
    </div>
  );
}

/**
 * The title row every page shares: what this page answers, and the one control
 * that belongs to it.
 *
 * Kept here rather than in `ui.tsx` because it is part of the shell's rhythm,
 * not a general primitive: panels inside a page still use `Card` and
 * `SectionLabel`.
 */
export function PageHead({ title, blurb, action }: {
  title: string; blurb?: string; action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
      <div className="min-w-0">
        <h1 className="font-serif text-2xl lg:text-3xl leading-tight">{title}</h1>
        {blurb && <p className="text-text-secondary text-sm mt-1 max-w-prose">{blurb}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
